import { Loader2, UserPlus, Trash2, ShieldCheck, Copy } from "lucide-react";
import { toast } from "sonner";
import { TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export const AdminsPanel = ({
  admins,
  currentEmail,
  newAdmin,
  setNewAdmin,
  creating,
  onCreate,
  onDelete,
  tempPassword,
}) => (
  <TabsContent value="admins" className="mt-6 space-y-6" data-testid="admins-panel">
    <div className="bg-white border border-border rounded-2xl p-8">
      <div className="flex items-center gap-2 text-ink">
        <UserPlus className="h-5 w-5 text-clay" />
        <h2 className="font-heading text-xl">Add an admin</h2>
      </div>
      <p className="mt-2 text-sm text-ink-muted max-w-2xl">
        <strong>Owners</strong> have full access. <strong>Editors</strong> can view analytics and
        (when available) manage Expert guides only — they can't see the rubric, coupons, settings or
        other admins. New admins get a temporary password and must set their own on first sign-in.
      </p>
      <div className="mt-5 grid gap-4 sm:grid-cols-[1fr_auto_auto]">
        <div>
          <Label className="text-ink font-medium">Email</Label>
          <Input
            type="email"
            data-testid="new-admin-email-input"
            value={newAdmin.email}
            onChange={(e) => setNewAdmin((a) => ({ ...a, email: e.target.value }))}
            placeholder="name@example.com"
            className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
          />
        </div>
        <div>
          <Label className="text-ink font-medium">Role</Label>
          <Select value={newAdmin.role} onValueChange={(v) => setNewAdmin((a) => ({ ...a, role: v }))}>
            <SelectTrigger data-testid="new-admin-role-select" className="mt-2 rounded-xl py-6 min-w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="editor">Editor</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button
            data-testid="create-admin-button"
            onClick={onCreate}
            disabled={creating || !newAdmin.email.trim()}
            className="bg-clay hover:bg-clay-hover text-white rounded-lg px-7 py-6 font-medium w-full"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
          </Button>
        </div>
      </div>

      {tempPassword && (
        <div className="mt-5 rounded-xl border border-forest/30 bg-forest/[0.06] p-4" data-testid="temp-password-box">
          <p className="text-sm text-ink-soft">
            Share this <strong>temporary password</strong> with the new admin securely. They'll be
            asked to change it on first sign-in. It won't be shown again.
          </p>
          <div className="mt-2 flex items-center gap-3">
            <code className="rounded-lg bg-white border border-forest/30 px-4 py-2 font-mono font-semibold tracking-wide text-forest">
              {tempPassword}
            </code>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard?.writeText(tempPassword);
                toast.success("Temporary password copied.");
              }}
              className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
            >
              <Copy className="h-4 w-4" /> Copy
            </button>
          </div>
        </div>
      )}
    </div>

    <div className="bg-white border border-border rounded-2xl p-8" data-testid="admins-list">
      <h2 className="font-heading text-xl text-ink mb-4">Admins</h2>
      <div className="divide-y divide-border">
        {admins.map((a) => (
          <div key={a.email} className="flex items-center justify-between gap-4 py-3" data-testid={`admin-row-${a.email}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-ink truncate">{a.email}</span>
                {a.email === currentEmail && <span className="text-xs text-ink-muted">(you)</span>}
              </div>
              <div className="text-xs text-ink-faint mt-0.5 flex items-center gap-2">
                <span className={`uppercase tracking-wide font-semibold ${a.role === "owner" ? "text-clay" : "text-forest"}`}>
                  {a.role}
                </span>
                {a.is_primary && (
                  <span className="inline-flex items-center gap-1 text-ink-muted">
                    <ShieldCheck className="h-3 w-3" /> primary
                  </span>
                )}
                {a.must_change_password && <span className="text-amberwarn">pending first login</span>}
              </div>
            </div>
            {!a.is_primary && a.email !== currentEmail && (
              <Button
                variant="ghost"
                size="sm"
                data-testid={`delete-admin-${a.email}`}
                onClick={() => onDelete(a.email)}
                className="text-clay hover:text-clay-hover shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  </TabsContent>
);
